import React, { useEffect, useState, useContext, useRef } from "react";
import Button from "@mui/material/Button";
import slEmblem from "../../images/logo_w_en.svg";
import ReactPlayer from "react-player";
import { useNavigate } from "react-router-dom";
import { ThemeAudioContext } from "../../components/ThemeAudioContext";
import axios from "axios";

const Landing = () => {
  const [playVideo, setPlayVideo] = React.useState(false);
  const [introVideoUrl, setIntroVideo] = useState("");
  const [languageSelectAudios, setLanguageSelectAudios] = useState([]);
  const navigate = useNavigate();
  const languageSelectAudioRef = React.useRef(null);
  const bgNarration = useRef(null);
  const isLanguageAudioPlaying = useRef(false);
  const {
    toggleAudio,
    enabled,
    setAudioSource,
    pauseThemeAudio,
    resumeThemeAudio,
    reduceThemeAudioVolume,
    restoreThemeAudioVolume,
  } = useContext(ThemeAudioContext);
  const navigateToVideo = () => navigate("/video");

  useEffect(() => {
    // 🔥 kill background audio
    if (window.__BG_AUDIO__) {
      try {
        window.__BG_AUDIO__.pause();
        window.__BG_AUDIO__.currentTime = 0;
      } catch (e) { }
      window.__BG_AUDIO__ = null;
    }

    // 🔥 kill narration audio
    if (window.__NARRATION_AUDIO__) {
      try {
        window.__NARRATION_AUDIO__.pause();
        window.__NARRATION_AUDIO__.currentTime = 0;
      } catch (e) { }
      window.__NARRATION_AUDIO__ = null;
    }

    // 🔥 also kill any DOM audio just in case
    document.querySelectorAll("audio").forEach((a) => {
      try {
        a.pause();
        a.currentTime = 0;
      } catch (e) { }
    });
  }, []);

  const onLanguageSelect = (langCode) => {
    const clickAudio = new Audio("https://res.cloudinary.com/giglantis/video/upload/v1777458322/LANGUAGE_r4uytc.mp3");
    clickAudio.volume = 1;
    clickAudio.play().catch((err) => {
      console.log(err);
    });
    stopLanguageSelectAudio();
    localStorage.setItem("language", langCode);
    const seenTutorial = localStorage.getItem("seenTutorial");
    stopAllAudio();
    if (seenTutorial === "1") {
      navigate("/video");
    } else {
      navigate("/tutorial");
    }
  };

  const videoReady = () => {
    setPlayVideo(true);
  };

  useEffect(() => {
    getColors();
    getVideos();
    getAudios();
  }, []);

  const getColors = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API + "Colors/getColors"
      );
      document.documentElement.style.setProperty(
        "--primary-color",
        response.data.primaryColor
      );
      document.documentElement.style.setProperty(
        "--secondary-color",
        response.data.secondaryColor
      );
    } catch (error) {
      console.error(error);
      console.log("There was an error!", error);
    }
  };

  const getVideos = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API + "Videos/getVideos"
      );
      setIntroVideo(response.data.introVideo);
    } catch (error) {
      console.error(error);
      console.log("There was an error!", error);
    }
  };

  const getAudios = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API + "Audios/getAudios"
      );
      setLanguageSelectAudios([
        response.data.languageSelectAudioSinhala,
        response.data.languageSelectAudioTamil,
        response.data.languageSelectAudio,
      ]);
    } catch (error) {
      console.error(error);
      console.log("There was an error!", error);
    }
  };

  useEffect(() => {
    startBackgroundNarrationAudio();

    if (languageSelectAudios.length > 0) {
      playLanguageSelectAudios();
    }

    return () => {
      stopLanguageSelectAudio();
    };
  }, [languageSelectAudios]);

  useEffect(() => {
    const handlePopState = () => {
      stopLanguageSelectAudio();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      stopLanguageSelectAudio();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const playLanguageSelectAudios = () => {
    // Prevent duplicate play
    if (isLanguageAudioPlaying.current) return;

    if (!languageSelectAudios.length) return;

    isLanguageAudioPlaying.current = true;

    stopLanguageSelectAudio();

    let currentIndex = 0;

    const playNext = () => {
      if (currentIndex >= languageSelectAudios.length) {
        bgNarration.current.volume = 0.5;
        languageSelectAudioRef.current = null;

        // Reset flag
        isLanguageAudioPlaying.current = false;

        return;
      }

      const audioUrl = languageSelectAudios[currentIndex];

      if (!audioUrl) {
        currentIndex++;
        playNext();
        return;
      }

      const audio = new Audio(audioUrl);
      audio.loop = false;

      languageSelectAudioRef.current = audio;

      audio.onended = () => {
        currentIndex++;
        playNext();
      };

      bgNarration.current.volume = 0.2;

      audio.play().catch(() => { });
    };

    playNext();
  };

  const stopLanguageSelectAudio = () => {
    if (languageSelectAudioRef.current) {
      languageSelectAudioRef.current.pause();
      languageSelectAudioRef.current.currentTime = 0;
      languageSelectAudioRef.current = null;
    }

    isLanguageAudioPlaying.current = false;

    bgNarration.current.volume = 0.5;
  };

  const startBackgroundNarrationAudio = () => {
    if (!bgNarration.current) {
      const audio = new Audio("https://res.cloudinary.com/giglantis/video/upload/v1777370841/1_Intro_Musiconly_Master_seqyym.mp3");
      window.__BG_AUDIO__ = audio;
      audio.loop = true;
      audio.volume = 0.5;
      bgNarration.current = audio;
    }

    bgNarration.current.play().catch((err) => {
      console.log("BG AUDIO BLOCKED:", err);
    });
  };

  const stopAllAudio = () => {
    if (bgNarration.current) {
      bgNarration.current.pause();
      bgNarration.current.currentTime = 0;
    }
  };

  return (
    <div className="video-page-container">
      {!playVideo && <div className="loading-black-bg"></div>}
      <div className="video-bg">
        <ReactPlayer
          className="video-container"
          url={introVideoUrl}
          loop={true}
          playing={playVideo}
          muted={true}
          controls={false}
          playsinline={true}
          onReady={() => videoReady()}
        />
        <div className="landing-container">
          <div className="center-container">
            <div className="landing-center-container">
              <div className="flex-column-container">
                <img className="sl-emblem" src={slEmblem} alt="" />
                <span className="landing-heading">VIRTUAL TOUR OF</span>
                <span className="landing-heading">PARLIAMENT OF SRI LANKA</span>
              </div>
              <div className="flex-column-container language-div">
                <span className="language-select-text">
                  SELECT LANGUAGE TO START TOUR
                </span>
                <Button type="button"
                  className="language-btn"
                  variant="contained"
                  onClick={() => onLanguageSelect("sl")}
                >
                  සිංහල
                </Button>
                <Button type="button"
                  className="language-btn"
                  variant="contained"
                  onClick={() => onLanguageSelect("tm")}
                >
                  தமிழ்
                </Button>
                <Button type="button"
                  className="language-btn"
                  variant="contained"
                  onClick={() => onLanguageSelect("en")}
                >
                  English
                </Button>
              </div>
            </div>
            <span
              className="background-video-link"
              onClick={() => {
                stopLanguageSelectAudio();
                navigateToVideo();
              }}
            >
              WATCH BACKGROUND VIDEO
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Landing;
